import React, { useEffect, useRef } from 'react';
import './index.css';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import App from './pages/index';
import Careers from './pages/careers';
import Contact from './pages/contact';
import Team from './pages/team';
import Order, { OrderThanks } from './pages/order';
import Privacy from './pages/legal/privacy';
import Terms from './pages/legal/terms';
import Refunds from './pages/legal/refunds';
import Cookies from './pages/legal/cookies';
import Accessibility from './pages/legal/accessibility';
import NotFound from './pages/404';
import Unsubscribe from './pages/unsubscribe';
import ChatbotTest from './pages/chatbottest';
import reportWebVitals from './reportWebVitals';

// Global smooth scroll with inertia
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const lenis = new Lenis({
  duration: reducedMotion ? 0 : 0.9,
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  smoothWheel: !reducedMotion,
});

// Exposed so pages can drive smooth scroll (e.g. tab links) and pause it
// behind modals. Lenis owns the scroll position, so plain window.scrollTo
// gets reverted; go through the instance instead.
if (typeof window !== 'undefined') window.__lenis = lenis;

function raf(time) {
  lenis.raf(time);
  requestAnimationFrame(raf);
}
requestAnimationFrame(raf);

const PAGE_TITLES = {
  '/': 'Korsa | Smart glasses with live captions and translation',
  '/order': 'Pre-order Korsa Glasses | Korsa',
  '/order/thanks': 'Pre-order confirmed | Korsa',
  '/careers': 'Careers | Korsa',
  '/contact': 'Contact us | Korsa',
  '/team': 'Our team | Korsa',
  '/terms': 'Terms of Service | Korsa',
  '/privacy': 'Privacy Policy | Korsa',
  '/refunds': 'Refunds and Cancellation | Korsa',
  '/cookies': 'Cookie Notice | Korsa',
  '/accessibility': 'Accessibility Statement | Korsa',
  '/unsubscribe': 'Unsubscribe | Korsa',
  '/chatbottest': 'Chatbot test | Korsa',
};

// Runs on every route change: back to the top, a title that names the page, and
// focus on the page's heading, so screen readers announce the new page. The
// first load is left alone: the browser already reads the page from the top.
function RouteChange() {
  const { pathname, hash } = useLocation();
  const firstLoad = useRef(true);
  useEffect(() => {
    // A link to /terms#pre-orders lands on that section instead of the top.
    const target = hash ? document.getElementById(hash.slice(1)) : null;
    if (target) {
      // On a fresh load Lenis hasn't measured the page yet, so wait a frame.
      requestAnimationFrame(() => {
        lenis.resize();
        lenis.scrollTo(target, { immediate: true, offset: -96 });
      });
    } else {
      lenis.scrollTo(0, { immediate: true });
    }
    document.title = PAGE_TITLES[pathname] || 'Page not found | Korsa';
    if (firstLoad.current && !target) {
      firstLoad.current = false;
      return;
    }
    firstLoad.current = false;
    const heading = target ? target.querySelector('h2') : document.querySelector('h1');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }, [pathname, hash]);
  return null;
}

// Logs accessibility problems to the browser console during development only.
if (process.env.NODE_ENV === 'development') {
  import('@axe-core/react').then(({ default: axe }) => axe(React, ReactDOM, 1000));
}

// The first landing of a browsing session goes straight to the product page.
// The URL is rewritten before the router mounts, so the home page never
// renders and then jumps. Anything after that, a second visit or the nav's
// Home link, falls through and renders home as normal, because the flag is
// already set. Storage can throw in private mode or with cookies blocked, in
// which case we leave the visitor on the home page.
const LANDED_KEY = 'korsa:landed';
try {
  if (window.location.pathname === '/' && !sessionStorage.getItem(LANDED_KEY)) {
    sessionStorage.setItem(LANDED_KEY, '1');
    window.history.replaceState(null, '', '/order');
  }
} catch (err) {
  /* no storage, show home */
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <RouteChange />
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/team" element={<Team />} />
        <Route path="/order" element={<Order />} />
        <Route path="/order/thanks" element={<OrderThanks />} />
        {/* Old reservation URLs. /reserve/thanks is the Stripe payment link's
            return URL, so it has to keep resolving for buyers coming back. */}
        <Route path="/reserve" element={<Navigate to="/order" replace />} />
        <Route path="/reserve/thanks" element={<Navigate to="/order/thanks" replace />} />
        <Route path="/purchase" element={<Navigate to="/order" replace />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/accessibility" element={<Accessibility />} />
        {/* Old legal URLs, still linked from emails and search results */}
        <Route path="/privacypolicy" element={<Navigate to="/privacy" replace />} />
        <Route path="/tos" element={<Navigate to="/terms" replace />} />
        {/* Pre-order terms were merged into the Terms of Service */}
        <Route path="/reservation-terms" element={<Navigate to="/terms#pre-orders" replace />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/chatbottest" element={<ChatbotTest />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
